import { environment } from 'src/environments/environment'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { Observable, of } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { CancellationPolicy } from 'src/app/shared/interfaces'
import { v4 as uuidv4 } from 'uuid'

import { Component, OnInit } from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { ProductGroup } from '../../product/product-group/product-group'
import { productGroupActions } from '../../product/product-group/product-group.actions'
import { productGroupSelectors } from '../../product/product-group/product-group.selectors'
import { ConfigurationOption } from '../configuration-option'
import { configurationOptionActions } from '../configuration-option.actions'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'
import { Product } from '../../product/product'
import { productActions } from '../../product/product.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { productSelectors } from '../../product/product.selectors'

@Component({
  selector: 'configuration-option-organization-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CancellationPoliciesTabComponent implements OnInit {
  public optionValue$: Observable<any>
  public configOption$: Observable<ConfigurationOption>
  public cancellationPolicies$: Observable<CancellationPolicy[]>
  public policies: CancellationPolicy[]
  public configFormValues$: Observable<any>
  public regCategoryFilters$: Observable<any>
  public regCategoryOptions$: Observable<any>
  public isCollapsed = false
  public isCollapsedCategory = false
  public isCollapsedRegCategoryFilter = false
  public productGroups$: Observable<ProductGroup[]>
  public products$: Observable<Product[]>
  public accessRights = AccessRights
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return (acc[curr] = true), acc
      else return acc
    },
    {}
  )

  public form: UntypedFormGroup

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private actions$: Actions,
    private configOptionService: ConfigurationOptionService
  ) {}

  get policyFormArray() {
    return this.form?.controls?.policies as UntypedFormArray
  }

  get valuesFormArray() {
    return this.policyFormArray.get('values') as UntypedFormArray
  }

  ngOnInit() {
    this.store.dispatch(productGroupActions.fetchMany({}))

    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            bookable: false,
            $or: [
              {
                allowAsLinkedProduct: null
              },
              {
                allowAsLinkedProduct: false
              }
            ]
          },
          join: [{ field: 'translations' }]
        })
      })
    )

    this.products$ = this.store
      .select(productSelectors.selectAll)
      .pipe(
        map((products) =>
          products.filter(
            (product) => !product.bookable && !product.allowAsLinkedProduct
          )
        )
      )

    this.productGroups$ = this.store.select(productGroupSelectors.selectAll)

    this.configFormValues$ = this.store
      .select(configurationOptionSelectors.selectAll)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((configOptions) => {
          return this.configOptionService.mapEntitiesToObject(configOptions)
        })
      )

    this.configOption$ = this.store.select(
      configurationOptionSelectors.selectByKey,
      'cancellation_policies'
    )

    this.cancellationPolicies$ = this.configOption$.pipe(
      filter((x) => Boolean(x)),
      map((option) => JSON.parse(option.value))
    )

    this.form = this.fb.group({
      policies: this.fb.array([])
    })

    this.cancellationPolicies$.pipe(take(1)).subscribe((policies) => {
      this.policies = policies
      for (let policy of policies) {
        this.addPolicyRow(policy)
      }

      this.form?.controls.policies.patchValue(policies)
    })
  }

  public getPolicyRow() {
    return this.fb.group({
      cancellationPolicyId: ['', [Validators.required]],
      cancelationProductId: [null, [Validators.required]],
      title_en: ['', [Validators.required]],
      title_nl: [''],
      title_fr: [''],
      title_de: [''],
      values: this.fb.array([])
    })
  }

  public getValuesRow() {
    return this.fb.group({
      productGroupIds: [null, [Validators.required]],
      payablePercentage: [null]
    })
  }

  public addPolicyRow(
    policy?: CancellationPolicy,
    shouldGenerateUUID: boolean = false
  ) {
    this.policyFormArray.push(this.getPolicyRow())
    if (policy) {
      const policyRow = this.policyFormArray.controls[
        this.policyFormArray.controls.length - 1
      ] as UntypedFormGroup

      for (let value of policy?.values) {
        const valuesRow = policyRow.controls.values as UntypedFormArray
        valuesRow.push(this.getValuesRow())
      }
    }
    if (shouldGenerateUUID) {
      this.generateUUID(this.policyFormArray.length - 1)
    }
  }

  public deletePolicyRow(index): void {
    this.policyFormArray.removeAt(index)
  }

  public addValuesRow(form) {
    const valuesArray = form.controls.values as UntypedFormArray
    valuesArray.push(this.getValuesRow())
  }

  public deleteValuesRow(form, j): void {
    const valuesArray = form.controls.values as UntypedFormArray
    valuesArray.removeAt(j)
  }

  public onSubmit() {
    if (!this.form.valid) {
      return
    }

    const configOptionValue = JSON.stringify(this.form.value?.policies)

    this.configOption$.pipe(take(1)).subscribe((configOption) => {
      if (!configOption) {
        this.store.dispatch(
          configurationOptionActions.createOne({
            entity: {
              key: 'cancellation_policies',
              value: configOptionValue
            }
          })
        )
      } else {
        this.store.dispatch(
          configurationOptionActions.updateOneById({
            id: configOption.id,
            params: {
              value: configOptionValue
            }
          })
        )
      }
    })

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }

  public generateUUID(index: number) {
    this.policyFormArray.controls[index].patchValue({
      cancellationPolicyId: uuidv4()
    })
  }

  public getPolicies(form) {
    return form.controls.policies.controls
  }

  public getValues(form) {
    return form.controls.values.controls
  }

  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [{ field: 'translations' }]
        })
      })
    )
  }
}
